/**
 * interpreter — the BLUEPRINT INTERPRETER (README §7.2).
 *
 * Walks `pages[entry].root`, resolves nodes → kit components, evaluates
 * `bind`/props against the render context, and wires `connections` to
 * component slots and the `host` bridge.
 *
 * The connection machinery is the Qt facet made real:
 *   emit(node, signal) → look up `connections[from == "node.signal"]`
 *     → `to: "host.<slot>"`      ⇒ call the host bridge (executor `host`)
 *     → `to: "page_args.<key>"`  ⇒ merge one page-local arg, which re-derives
 *        every selector that reads it (a bar tap selecting a day)
 *     → `to: "<node>.<slot>"`    ⇒ deliver into that node's SLOT INBOX, which
 *        the target component reads as a prop and reacts to.
 * `mode: sync` delivers immediately; `async` defers a tick; `rate:
 * debounce(n)` coalesces; `backpressure: latest` keeps only the newest
 * payload — all of it decided by the blueprint, none of it by a component.
 *
 * A connection MAY also carry a `payload` MAP (§5): a template evaluated in
 * the bind grammar against the EMITTING node's own scope and merged OVER the
 * raw signal payload. That is what decouples an emitter from a slot — a plain
 * `Button.activated {action}` can feed `host.download {url,filename}` without
 * inventing a DownloadButton — and it is why every emit carries the scope it
 * fired in (a card's row, a composition's props) rather than only its node id.
 *
 * ITEM SCOPE is the other half: a grid renders its `card` template once per
 * row inside a scope where `item` IS that row, so a card binds "item.name"
 * exactly the way a page binds "focusEntry.name" (see `renderTemplate`). Any
 * node can open the same scope for itself with `repeat` (§4.1).
 *
 * PROPS SCOPE is that mechanism one layer up: a `composed` type (an L3 Domain
 * component, README §6.2) has no native code — it expands its `composition`
 * node tree inside a scope where `props` IS the parameter interface the outer
 * node passed and `data` IS what the outer node's `bind` resolved to. Signals
 * out of the body are re-routed to the outer node's id, and slots delivered to
 * the outer node are forwarded to the inner node that declared them.
 *
 * Fail-closed (§7.6), refined by §4.5: a type this build cannot render
 * degrades to its DECLARED FALLBACK (function preserved, form degraded), a
 * `null` fallback omits the node, and only a type with no fallback at all
 * renders the visible placeholder — which, with an unknown selector op or
 * named derive, is also reported through `onReport` so the studio can show
 * what a page could not honour.
 */
import { usesPageHeader } from './pageHeaderContext';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { ViewIRContext, useViewIR } from './viewIRContext';
import { bodyHorizontalInset } from './layout';
import { slotReducer } from './slots';
import { propSpecFor, resolveComponent } from './registry';
import { resolveProps, parseRate } from './props';
import { evalWhen, getPath, pathRoot, resolveValue } from './paths';
import { UnsupportedComponent } from './components/Unsupported';
import { SkeletonPlate } from './components/feedback';
import { skeletonShapeFor } from './components/ui';
import { createAwaitConfirm, AWAIT_CONFIRM_DEFAULTS } from './components/hostConfirm';

const EMPTY_SLOTS = {};
const NO_STACK = [];

/**
 * The ROOT a `bind` reads from, in either form the grammar allows — the dotted
 * string ("focusEntry.images") or the object one ({bind} / {$} / {agg}). Only
 * one caller needs it (the skeleton rule, which must recognise a `pending`
 * bind), and it must recognise it through BOTH forms or a host-owned in-flight
 * row would shimmer away depending on how its bind happened to be written.
 */
function bindRoot(bind) {
  if (typeof bind === 'string') return pathRoot(bind);
  if (bind && typeof bind === 'object') {
    const expr = bind.bind ?? bind.$ ?? bind.agg?.of ?? bind.of;
    if (typeof expr === 'string') return pathRoot(expr);
  }
  return null;
}

/**
 * The SCOPE a node opens around a sub-tree: `{ ctx, emit, slots, composing }`.
 *
 * Two things open one, for the same reason — a sub-tree has no blueprint id,
 * so it has no connections of its own and its parent is its wiring:
 *   · a grid around its `card` template, and any node carrying `repeat`:
 *     `ctx` gains `item` = the current row, `emit` re-routes to the owner that
 *     holds the row's identity;
 *   · a COMPOSED type around its `composition` body: `ctx` gains `props` (the
 *     evaluated parameter interface) and `data` (the outer node's bind),
 *     `emit` maps inner signal names to the composition's outward ones,
 *     `slots` delivers the outer node's inbox to the inner node that declared
 *     it, and `composing` is the expansion stack the cycle/depth guard reads.
 *
 * One context for both: a composition inside a card template must still see
 * `item`, and a scope always builds its value from the enclosing one.
 */
const NodeScope = createContext(null);

/** Max composed-inside-composed nesting. Deeper is a design smell, not a page. */
const MAX_COMPOSITION_DEPTH = 3;

/**
 * THE PAGE-LOCAL SCROLL SINK (KG-28) — the `scrollIntoView` slot every node
 * has, without any node declaring it.
 *
 * Prod's mind map taps a branch and the matching topic card slides into view;
 * `ux_rule.md` is explicit that this is NOT navigation (不触发 host 导航). The
 * connection grammar could address `host.*` or `<node>.<slot>`, and a scroll is
 * neither a host op nor something a component should own — every component in
 * the kit would need the same slot, wired to the same three lines of DOM. So
 * the INTERPRETER owns it: `topicMap.branchActivated → topicCard.scrollIntoView`
 * is expressible against any node, and the host is not involved.
 *
 * The wrapper is `display: contents`, so the page lays out exactly as it did —
 * and it is mounted ONLY once a payload has actually been delivered, so a
 * blueprint that never scrolls anything gets byte-identical DOM. Because a
 * `display: contents` box is not scrollable itself, the scroll targets the
 * first real child.
 *
 * `seq` is the identity of a DELIVERY, not of a payload: tapping the same
 * branch twice must scroll twice, and comparing payloads would swallow the
 * second tap.
 */
function ScrollSink({ signal, children }) {
  const ref = useRef(null);
  const seen = useRef(null);
  useEffect(() => {
    if (!signal || signal.seq === seen.current) return;
    seen.current = signal.seq;
    const el = ref.current?.firstElementChild || ref.current;
    el?.scrollIntoView?.({
      behavior: signal.payload?.behavior || 'smooth',
      block: signal.payload?.block || 'center',
    });
  }, [signal]);
  return <div ref={ref} style={{ display: 'contents' }}>{children}</div>;
}

/**
 * WHICH repetition a `scrollIntoView` delivery is for.
 *
 * A `repeat` node renders under derived ids (`topicCard__r3`), so a connection
 * written against the blueprint id `topicCard` lands in ONE inbox that every
 * repetition can see. The emitting repeat already puts its ordinal on the
 * signal (`{index: i, …}`), so the payload says which row it meant; a delivery
 * with no index at all addresses the first repetition, which is the only
 * reading that does not scroll six cards at once.
 */
function scrollForRow(inbox, i) {
  const signal = inbox?.scrollIntoView;
  if (!signal) return undefined;
  const want = signal.payload?.index;
  const wanted = want == null ? 0 : Number(want);
  return wanted === i ? signal : undefined;
}

/**
 * `repeat` as an OBJECT may carry its own overflow rule:
 * `{"bind": "focusEntry.topics", "limit": 6, "moreLabel": "+{n} more below"}`.
 *
 * Prod caps every one of these lists — 6 map branches, 2 detail bullets, 2
 * action chips — and says so in a trailing line. Without a cap a nine-bullet
 * topic renders all nine and the design's rhythm is gone; without the line the
 * user is not told anything was left out. It lives on `repeat` rather than as
 * a node prop because it is a property of the ITERATION, and a node's props
 * belong to the component being repeated.
 */
function repeatLimit(spec) {
  if (!spec || typeof spec !== 'object' || Array.isArray(spec)) return 0;
  const n = Number(spec.limit);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

/**
 * INSPECT wrapper (studio only). With `inspect` set, every node is wrapped in
 * a `display: contents` marker — no box, so the page lays out EXACTLY as it
 * does without it — carrying:
 *   data-vi-node  the blueprint id, so a click overlay can resolve what was
 *                 tapped with `elementsFromPoint(...).closest()`;
 *   data-vi-dim   "this subtree is not part of the focus", which the studio's
 *                 stylesheet grays out (it styles the marker's CHILDREN, the
 *                 real boxes, since a `display: contents` element paints
 *                 nothing itself);
 *   data-vi-box   "THIS is the module the view is about" — the studio outlines
 *                 it, and measures the same children to anchor its wires, so
 *                 the drawn box and the wire endpoint can never disagree.
 *
 * `inspect.dim` is the FRONTIER — the outermost nodes to gray — not "every
 * node that is not focused": nested dimming would multiply opacities until a
 * deep subtree vanished. See studio/focusModel.js.
 *
 * `inspect.box` is one id OR A SET of them: a data pick connects to every card
 * that renders it, which is often several at once, and each of those wires
 * carries its own box. A string still boxes the one node.
 */
function inspectWrap(inspect, nodeId, element, anonymous = false) {
  if (!inspect || !nodeId || anonymous) return element;
  const boxed = inspect.box instanceof Set ? inspect.box.has(nodeId) : inspect.box === nodeId;
  return (
    <div
      style={{ display: 'contents' }}
      data-vi-node={nodeId}
      data-vi-dim={inspect.dim?.has(nodeId) ? '1' : undefined}
      data-vi-box={boxed ? '1' : undefined}
    >
      {element}
    </div>
  );
}

/**
 * The runtime services a node needs when it is rendered OUTSIDE a page — the
 * studio previews a composition on its own, with no blueprint around it.
 * Everything a page provides is absent, and absent must mean "nothing routed",
 * never a crash: a preview is the same components under the same rules, minus
 * connections and minus a blueprint.
 */
const NO_RUNTIME = {
  blueprint: {},
  ctx: {},
  slotState: EMPTY_SLOTS,
  inspect: null,
  emit: () => {},
  report: null,
  renderById: () => null,
  renderNode: (node, key) => (node ? <IRNode key={key} nodeId={key} node={node} /> : null),
  renderTemplate: null,
  // No blueprint ⇒ no wire is connected. A component that gates an affordance on
  // connectivity (a Card's tap/chevron, §11) falls back to its own default.
  isConnected: () => false,
};

/**
 * A fail-closed marker that also REPORTS itself once, so the studio's
 * "fail-closed report" lists what the page could not honour rather than
 * leaving it to be spotted as a red box on a page nobody opened.
 */
function ReportedUnsupported({ report, issue, what, name, detail }) {
  useEffect(() => { report?.(issue); }, [report, issue]);
  return <UnsupportedComponent what={what} name={name} detail={detail} />;
}

/**
 * The `composition` body of a COMPOSED type, rendered under a props scope.
 *
 * Exported because the studio previews a composed component the same way the
 * runtime renders it — there is no other honest preview of a component that
 * has no code of its own.
 *
 *   type    the composed component_id (also the cycle-guard key)
 *   props   its EVALUATED props — `props.*` inside the body
 *   data    what its `bind` resolved to — `data.*` inside the body
 *   emit    the OUTER node's emit; inner signals are mapped onto it
 *   slots   the OUTER node's slot inbox, forwarded per `slots[].forwardsTo`
 */
export function ViewIRComposition({
  type, nodeId, def, props = {}, data, ctx = {}, emit = null, slots = null, stack = NO_STACK,
}) {
  const scoped = useMemo(() => {
    const map = {};
    for (const s of def?.slots || []) {
      const inbox = slots?.[s.name];
      if (!inbox || !s.forwardsTo) continue;
      const dot = String(s.forwardsTo).indexOf('.');
      if (dot < 0) continue;
      const key = s.forwardsTo.slice(0, dot);
      const slot = s.forwardsTo.slice(dot + 1);
      map[key] = { ...(map[key] || {}), [slot]: inbox };
    }
    // An inner signal reaches the blueprint under the OUTER node's name: a
    // composition's ports are its own, never its parts' (mirror of the grid
    // remapping a card's `activated` to `itemActivated`).
    const outward = {};
    for (const s of def?.signals || []) if (s?.from) outward[s.from] = s.name;
    const scopedCtx = { ...ctx, props, data };
    return {
      ctx: scopedCtx,
      // the scope rides along with the signal, so a connection's `payload` map
      // can read the `props.*` / `data.*` the body was expanded under. A part
      // that emits a PARTIAL scope (a chart forwarding the tapped row as
      // `{item}`) MERGES over the body's scope rather than replacing it, so the
      // wire still sees `page_args.*`/`data.*` alongside the row — the same
      // `{...pageCtx, item}` shape a grid card's `renderTemplate` rides up with.
      emit: (signal, payload, emitCtx) => emit?.(
        outward[signal] || signal, payload, emitCtx ? { ...scopedCtx, ...emitCtx } : scopedCtx,
      ),
      slots: map,
      composing: [...stack, type],
      // The body carries NO blueprint identity — one composed node is ONE
      // module to the studio, the way it is to the blueprint. Marking its
      // parts would hand the inspect overlay ids no blueprint declares, and a
      // click on the ring would resolve to a part instead of the node the page
      // wired. (A grid's card template is the opposite case and stays marked:
      // there the studio deliberately selects an individual row.)
      anonymous: true,
    };
  }, [def, ctx, props, data, emit, slots, stack, type]);

  return (
    <NodeScope.Provider value={scoped}>
      <IRNode nodeId={`${nodeId}__composed`} node={def.composition} />
    </NodeScope.Provider>
  );
}

/**
 * One blueprint node → what this build renders for it. Recursion happens
 * through `children` (ids or inline nodes), through `renderById` /
 * `renderNode` for the declarative sections a detail page carries, through
 * `renderTemplate` for a grid's per-row card, and through `repeat` /
 * `composition` for the two node operators.
 */
function IRNode({ nodeId, node: inlineNode }) {
  const rt = useViewIR() || NO_RUNTIME;
  const scope = useContext(NodeScope);
  const node = inlineNode || rt.blueprint?.components?.[nodeId];
  // Inside a card template `item` IS the row and inside a composition `props`
  // IS the parameter interface, so every bind below — the node's own `bind`,
  // its props, its children — resolves against the scoped context.
  const ctx = scope?.ctx || rt.ctx;
  // The third argument is the SCOPE the signal fired in — what a connection's
  // `payload` map is evaluated against. A node that has one of its own passes
  // it up; a plain node hands over the context it rendered under.
  const emit = scope?.emit || ((signal, payload, emitCtx) => rt.emit(nodeId, signal, payload, emitCtx || ctx));

  if (!node) {
    return <UnsupportedComponent what="node" name={nodeId} detail="not declared in blueprint.components" />;
  }

  // ── REPEAT: one render per row of a bound array, each under an item scope.
  // Generalised off the grid (`renderTemplate` was the only caller) so ANY
  // node can iterate. `when` is evaluated INSIDE the row scope below, which is
  // what lets a repeat filter per row rather than only gate the whole list.
  if (node.repeat !== undefined && node.repeat !== null) {
    const rows = typeof node.repeat === 'string' ? getPath(ctx, node.repeat) : resolveValue(node.repeat, ctx);
    const all = Array.isArray(rows) ? rows : [];
    const limit = repeatLimit(node.repeat);
    const list = limit > 0 ? all.slice(0, limit) : all;
    const overflow = all.length - list.length;
    // ONE inbox for the whole repetition (see `scrollForRow`): a connection is
    // written against the blueprint id, and the repetitions render under ids
    // the blueprint has never heard of.
    const inbox = rt.slotState?.[nodeId];
    const once = { ...node };
    delete once.repeat;
    const items = list.map((item, i) => {
      const key = `${nodeId}__r${i}`;
      // `index` is the row's ORDINAL in the bind grammar — the one fact about a
      // repetition that is not a field of its row (paths.js SCOPE_ROOTS).
      const rowCtx = { ...ctx, item, index: i };
      return (
        <NodeScope.Provider
          key={key}
          value={{
            ...(scope || {}),
            ctx: rowCtx,
            // Addressed to the CLONE, not to everything under it: a row scope
            // is inherited by every descendant, and an unaddressed sink would
            // wrap each of them and scroll the same card five times.
            scrollSlot: scrollForRow(inbox, i),
            scrollFor: key,
            // the row's INDEX rides along, so one connection can tell which
            // repetition fired without the node carrying an identity of its own
            // — and so does the ROW SCOPE, which is what lets that connection's
            // `payload` map read `item.*` of the repetition that fired
            emit: (signal, payload, emitCtx) => emit(signal, { index: i, ...(payload || {}) }, emitCtx || rowCtx),
          }}
        >
          <IRNode nodeId={key} node={once} />
        </NodeScope.Provider>
      );
    });
    if (overflow > 0 && node.repeat.moreLabel) {
      // `{n}` is what was LEFT OUT — the number prod prints ("+2 more topics
      // below"). `total` and `shown` are there so a blueprint can phrase it the
      // other way round without a derive.
      const label = resolveValue(node.repeat.moreLabel, {
        ...ctx, n: overflow, total: all.length, shown: list.length,
      });
      items.push(
        <IRNode
          key={`${nodeId}__more`}
          nodeId={`${nodeId}__more`}
          node={{ type: 'Text', variant: 'caption', props: { value: label, tone: 'text-3' } }}
        />,
      );
    }
    return items;
  }

  // ── WHEN: a render predicate. Falsy renders NOTHING — an absent section is
  // not an error, so it must never draw the fail-closed marker.
  if (node.when !== undefined && !evalWhen(node.when, ctx)) return null;

  // ── the three-way resolution: native kit · composition · declared fallback.
  const resolved = resolveComponent(node.type, ctx?.target_build || null);
  if (resolved.status === 'omit') return null;
  if (resolved.status === 'unknown') {
    return (
      <UnsupportedComponent
        what="component" name={node.type}
        detail={`node "${nodeId}" — ${resolved.reason || 'no implementation and no fallback'}`}
      />
    );
  }
  const kit = resolved.kit;

  // `bind` is either a dotted read ("focusEntry.images") or the object form
  // the cover_json grammar also allows ({bind} / {$} / {agg}) — §4.2.
  const data = node.bind === undefined || node.bind === null
    ? undefined
    : (typeof node.bind === 'string' ? getPath(ctx, node.bind) : resolveValue(node.bind, ctx));
  if (data && data.__unsupported) {
    return <UnsupportedComponent what={data.__unsupported} name={data.name} detail={`bind "${node.bind}"`} />;
  }

  /**
   * THE ONE SKELETON RULE (loading-scheme §2).
   *
   * The feed has not arrived, so every DATA-BOUND node draws the shape of what
   * is coming instead of the shape of nothing. `bind` is the whole test: a node
   * that binds is a node whose content is the feed's, and a node that does not
   * — the page shell, a heading, a capture button, any authored copy — renders
   * immediately, which is what makes a widget open like a native page instead
   * of a spinner.
   *
   * Recursion is top-down, so the skeleton lands on the HIGHEST bound node and
   * its subtree is never walked: one rule covers a leaf and a whole module.
   *
   * `pending` is the exception, and it is not an edge case — a host-owned
   * in-flight capture is REAL and known at mount, so a collection bound to
   * `pending.adds` keeps drawing its live rows through the wait. Skeletonising
   * it would hide the one thing on the page that is actually happening.
   */
  if (ctx?.__loading && node.bind !== undefined && node.bind !== null && bindRoot(node.bind) !== 'pending') {
    return inspectWrap(rt.inspect, nodeId, <SkeletonPlate shape={skeletonShapeFor(node.type)} />, scope?.anonymous);
  }

  // Props resolve against the node's OWN declared type (its dataProps), even
  // when a fallback ends up rendering them — a fallback substitutes pixels,
  // never the meaning of the blueprint's bindings.
  const own = resolveProps(node.props, propSpecFor(node.type), ctx);
  const slots = (node.key && scope?.slots?.[node.key]) || rt.slotState?.[nodeId] || EMPTY_SLOTS;
  // The universal sink (KG-28), resolved once for both render paths below: the
  // node's own inbox, or — for a `repeat` clone, whose id the blueprint has
  // never heard of — this repetition's share of the base node's inbox.
  const scrollTo = slots.scrollIntoView || (scope?.scrollFor === nodeId ? scope.scrollSlot : undefined);
  const withSink = (el) => (scrollTo ? <ScrollSink signal={scrollTo}>{el}</ScrollSink> : el);

  // ── a COMPOSED type expands its body, guarded against cycles and depth.
  if (resolved.status === 'composition') {
    const stack = scope?.composing || NO_STACK;
    const cyclic = stack.includes(resolved.type);
    if (cyclic || stack.length >= MAX_COMPOSITION_DEPTH) {
      const what = cyclic ? 'composition cycle' : 'composition depth';
      return (
        <ReportedUnsupported
          report={rt.report} what={what} name={resolved.type}
          issue={{ __unsupported: what, name: resolved.type, node: nodeId }}
          detail={`node "${nodeId}" — ${cyclic ? [...stack, resolved.type].join(' → ') : `deeper than ${MAX_COMPOSITION_DEPTH}`}`}
        />
      );
    }
    return inspectWrap(rt.inspect, nodeId, withSink(
      <ViewIRComposition
        type={resolved.type} nodeId={nodeId} def={kit.composed}
        props={own} data={data} ctx={ctx} emit={emit} slots={slots} stack={stack}
      />,
    ), scope?.anonymous);
  }

  // A fallback may REMAP props when the shapes genuinely differ; the remap is
  // written in the same grammar a composition body uses, over `props.*` (what
  // the blueprint passed) and `data.*` (what its bind resolved to).
  const props = resolved.remap
    ? { ...own, ...resolveProps(resolved.remap, kit, { ...ctx, props: own, data }) }
    : own;

  const childItems = (node.children || []).map((child, i) => {
    const childId = typeof child === 'string' ? child : `${nodeId}__child${i}`;
    const childNode = typeof child === 'string' ? rt.blueprint?.components?.[child] : child;
    return {
      id: childId,
      region: childNode?.region || null,
      type: childNode?.type || null,
      node: childNode,
      // `bare` is read UNRESOLVED and deliberately: it is a layout opt-out the
      // author writes as a literal (`"bare": true`), and the container asking
      // the question — PageShell, deciding whether this child gets a panel —
      // renders before the child resolves its own props.
      bare: childNode?.props?.bare === true,
      // The child's own `when`, answered ONCE here as well: a container that
      // lays its children out ITSELF (Overlay's absolute layers, the shell's
      // fixed action bar) otherwise wraps a node that renders nothing in a box
      // that renders something. Pure and idempotent — same ctx, same answer as
      // the child's own gate, so the child stays the authority on itself.
      hidden: childNode?.when !== undefined && !evalWhen(childNode.when, ctx),
      element: <IRNode key={childId} nodeId={childId} node={typeof child === 'string' ? null : child} />,
    };
  });

  const Component = kit.component;
  // Whether one of THIS node's signals is wired out on this page — the web twin
  // of Flutter's `scope.connected(signal)` (`controller.isConnected(node.id,
  // signal)`). A component reads it to withhold an affordance it has no
  // destination for: an unwired Card is inert (no tap, no chevron — CONTRACT
  // §11). General on purpose — other components may gate on connectivity later.
  const connected = (signal) => (rt.isConnected ? rt.isConnected(nodeId, signal) : false);
  // Is this node's emit RE-ROUTED by an enclosing scope — a grid/collection
  // tile, a `repeat` row, a composition body? Then the scope IS the wiring (a
  // web collection routes a tile's tap through the card's own handler), so the
  // node is interactive even with no blueprint wire of its own. A Card reads
  // `rerouted` for tappability (button vs inert box) and `connected('activated')`
  // for the standalone-only "tap to open" chevron (§11).
  const rerouted = Boolean(scope?.emit);
  const element = (
    <Component
      nodeId={nodeId}
      node={node}
      variant={node.variant}
      props={props}
      data={data}
      ctx={ctx}
      emit={emit}
      connected={connected}
      rerouted={rerouted}
      slots={slots}
      childItems={childItems}
      renderById={rt.renderById}
      renderNode={rt.renderNode}
      renderTemplate={rt.renderTemplate}
    >
      {childItems.map((c) => c.element)}
    </Component>
  );
  return inspectWrap(rt.inspect, nodeId, withSink(element), scope?.anonymous);
}

/**
 * DOES THIS PAGE OWN THE FIRST RUN?
 *
 * A page may declare its own wardrobe-wide empty — the `region: "empty"`
 * EmptyState, in its `first-run` or `onboarding` reading — and a collection on
 * that same page ALSO draws a first-run state when it has no rows. On a widget
 * that genuinely holds nothing both are true at once, so the page rendered two
 * empty states stacked: "Nothing here yet" over the one that actually teaches
 * the user what the widget does. Two answers to one question, the weaker one
 * first.
 *
 * So the page says once, here, that it owns that reading, and the collections
 * stand down (`collections.jsx`, and only when the widget really is empty —
 * see `widgetHoldsNothing`). It is a PAGE-LEVEL declaration, not a widget-level
 * one: a page that declares no such node keeps the old behaviour exactly, which
 * is what makes this safe for the seven blueprints that gate their empties some
 * other way.
 *
 * `region: "empty"` is the marker rather than "any EmptyState on the page",
 * because that region is precisely how a blueprint says "this is the page's own
 * empty" — an inline one inside a collection is that collection's business.
 */
const FIRST_RUN_KINDS = new Set(['first-run', 'onboarding']);

function pageOwnsFirstRun(blueprint, page) {
  if (!page) return false;
  const byId = blueprint?.components || {};
  const seen = new Set();
  const walk = (ref) => {
    if (typeof ref === 'string') {
      if (seen.has(ref)) return false; // a cyclic children graph must not hang
      seen.add(ref);
    }
    const node = typeof ref === 'string' ? byId[ref] : ref;
    if (!node || typeof node !== 'object') return false;
    if (node.type === 'EmptyState' && node.region === 'empty'
      && FIRST_RUN_KINDS.has(node.props?.kind || node.variant || 'first-run')) return true;
    return (Array.isArray(node.children) ? node.children : []).some((c) => walk(c));
  };
  return walk(page.root);
}

/**
 * THE WIRES ONE EMIT FIRES — `"<node>.<signal>"`, plus the OP-SCOPED form.
 *
 * `host.hostResult` is one signal for every answering host op, which is exactly
 * right while a page has ONE answering slot (`pickMedia`) and a quiet
 * mis-routing the moment it has two: a `download` that answers would land in
 * the picker's `setValue`. So a connection may name the op it means —
 * `from: "host.hostResult(pickMedia)"` — and only the matching answer fires it.
 *
 * The bare `host.hostResult` still matches EVERY op, unchanged: every shipped
 * blueprint writes it that way, and an op-scoped selector is an author saying
 * something MORE specific, never a new requirement. Both forms fire when both
 * are declared, which is what keeps the two readings composable.
 */
function connectionsFor(index, nodeId, signal, payload) {
  const plain = index.get(`${nodeId}.${signal}`) || [];
  const op = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload.op : null;
  if (!op) return plain;
  const scoped = index.get(`${nodeId}.${signal}(${op})`);
  return scoped?.length ? [...plain, ...scoped] : plain;
}

/**
 * THE CONNECTION-LEVEL `when` PAYLOAD FILTER (README §5).
 *
 * A connection MAY carry `when: {field: value, …}` — an AND of shallow equality
 * constraints matched against the EMITTING SIGNAL'S OWN PAYLOAD. The wire fires
 * only when EVERY field is PRESENT in the payload and `===` the given value.
 *
 * It is the sibling of the `(op)` selector above: `host.hostResult(pickMedia)`
 * scopes an answering signal by its `op`, and `when` scopes a signal that
 * carries NO `op` by a field it does carry — `host.captureResult` cannot be
 * op-filtered, so a Save (`{dispatched: true}`) can drive an optimistic row
 * that a cancel (`{dispatched: false}`) must NOT. Grammar, not a component.
 *
 * A MISSING payload key is not a match — absent is not equal, so a wire asking
 * for `{dispatched: true}` never fires on a signal that carries no `dispatched`.
 * A connection with NO `when` (or an empty one) is unaffected — the return is
 * byte-identical to today, so every shipped blueprint is untouched.
 */
function connectionWhenMatches(when, payload) {
  if (!when || typeof when !== 'object' || Array.isArray(when)) return true;
  const keys = Object.keys(when);
  if (!keys.length) return true;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(payload, key)) return false;
    if (payload[key] !== when[key]) return false;
  }
  return true;
}

/**
 * ViewIRPage — mount one page of a blueprint over one render context.
 *
 * Props:
 *   blueprint  the parsed `<widget>.view.json`
 *   ctx        the render context from `buildRenderContext`
 *   pageId     which page to render (defaults to the `entry: true` page)
 *   host       the host bridge: `{ openItem, openPage, …, }` — the studio
 *              passes a mock, the app passes its real widget host
 *   onPageArgs (patch) → the runtime merges page-local args (a bar tap
 *              selecting a day) so selectors re-evaluate
 *   onReport   (issue) → unsupported components / derives, for the studio
 *   inspect    studio inspect mode: `{ dim: Set<nodeId>, box?: nodeId }`. Marks
 *              every node with its blueprint id (for click-to-select), grays
 *              the given subtrees and flags the one node the view is about.
 *              Omit it and the render is byte-for-byte the app's.
 *   interaction a host-owned `{ slotState, dispatch, ui, setUi }` (see slots.js)
 *              that several pages can SHARE — the studio's strip renders one
 *              widget as four cards and they must agree about the applied
 *              filter. Omitted (the app), the page owns its own inbox and its
 *              components their own local selection, exactly as before.
 */
/** 挂载蓝图页面，并按协议版本处理非 PageShell 页面的兜底水平留白。 */
export function ViewIRPage({
  blueprint, ctx, pageId, host, onPageArgs, onReport, onTrace,
  inspect = null, interaction = null,
}) {
  const [ownSlots, ownDispatch] = useReducer(slotReducer, {});
  const slotState = interaction?.slotState || ownSlots;
  const dispatch = interaction?.dispatch || ownDispatch;
  const seq = useRef(0);
  const timers = useRef(new Map());

  const pages = useMemo(() => (Array.isArray(blueprint?.pages) ? blueprint.pages : []), [blueprint]);
  const page = useMemo(
    () => pages.find((p) => p.id === pageId) || pages.find((p) => p.entry) || pages[0] || null,
    [pages, pageId],
  );

  /**
   * The context every node on THIS page sees: the one the runtime built, plus
   * the single fact only the page knows — that it declares the widget-wide
   * first-run empty. It rides on the context because the context is what every
   * component is already handed, and because the two layers that must agree
   * about it (this page's EmptyState and the collections under it) have no
   * other channel between them.
   */
  const pageCtx = useMemo(
    () => (pageOwnsFirstRun(blueprint, page) ? { ...ctx, __pageFirstRun: true } : ctx),
    [blueprint, page, ctx],
  );

  /**
   * from "node.signal" → the connections that fire on it, ON THIS PAGE.
   *
   * `connections[]` is blueprint-global, which is right for almost all of them
   * — a wire between two nodes only fires when both are on screen anyway. The
   * exception is every wire whose emitter is `host`: `host.dataReceived` fires
   * on EVERY page, so notes-taker's `host.dataReceived → host.declareNavAction`
   * re-declares the index's search button while the detail page is open, where
   * prod declares it from `index.html` only. `page` is the fix and it is a
   * property of the WIRE, not of a node: one emitter, different meanings per
   * page. A string or a list of page ids; absent ⇒ every page, so every
   * existing blueprint is untouched.
   */
  const index = useMemo(() => {
    const m = new Map();
    const here = page?.id ?? null;
    for (const c of blueprint?.connections || []) {
      if (!c?.from || !c?.to) continue;
      if (c.page !== undefined && c.page !== null) {
        const wanted = Array.isArray(c.page) ? c.page : [c.page];
        if (!wanted.some((p) => String(p) === String(here))) continue;
      }
      const list = m.get(c.from) || [];
      list.push(c);
      m.set(c.from, list);
    }
    return m;
  }, [blueprint, page]);

  // Stable handles so `emit` never has to be rebuilt when the host, the trace
  // sink or the page-args setter changes identity mid-interaction. They are
  // synced in an effect (never during render) and only ever read from event
  // handlers, which is exactly what a ref is for.
  const emitRef = useRef(null);
  const hostRef = useRef(host);
  const traceRef = useRef(onTrace);
  const pageArgsRef = useRef(onPageArgs);
  // the page context a connection's `payload` map falls back to when the
  // emitting node opened no scope of its own
  const ctxRef = useRef(pageCtx);
  useEffect(() => { ctxRef.current = pageCtx; }, [pageCtx]);
  useEffect(() => { hostRef.current = host; }, [host]);
  useEffect(() => { traceRef.current = onTrace; }, [onTrace]);
  useEffect(() => { pageArgsRef.current = onPageArgs; }, [onPageArgs]);

  useEffect(() => () => {
    for (const t of timers.current.values()) clearTimeout(t);
    timers.current.clear();
  }, []);

  /**
   * THE `host.logData` `awaitConfirm` LADDER, WIRED INTO THE CONNECTION DISPATCH.
   *
   * `hostConfirm.js` owns the three-state ladder (flash pending, watch the pushed
   * feed for the written value, give up honestly after a while) but is inert
   * until something arms it. THIS is where a `<ctrl>.committed → host.logData`
   * connection carrying `awaitConfirm` arms it — so the ladder is a property of
   * the runtime, not of a widget that hand-rolls it.
   *
   * One ladder PER SOURCE NODE (the node that emitted into `host.logData`), so
   * two forms on one page confirm independently and a second write from the same
   * control supersedes its own first. Each ladder's `onStatus` does the three
   * things the source control and the blueprint need and the host contract does
   * not carry:
   *   · pending / settled → deliver the interpreter-owned `submitStatus` slot to
   *     the SOURCE node (a spinner while the write is in flight — the same seam
   *     the interpreter owns `scrollIntoView` through, a universal slot no
   *     component declares);
   *   · confirmed → emit the blueprint-visible `<sourceNode>.confirmed {value}`,
   *     so a page can wire `goalField.confirmed → goalSheet.close`;
   *   · timeout → clear the spinner and emit NOTHING (the sheet stays open).
   * The value carried by `confirmed` is the value the source control committed,
   * captured at arm time (`entry.value`).
   */
  const confirmLadders = useRef(new Map());

  const deliverSubmitStatus = useCallback((sourceNodeId, submitting) => {
    seq.current += 1;
    dispatch({
      type: 'slot', nodeId: sourceNodeId, slot: 'submitStatus',
      payload: { submitting }, seq: seq.current, from: '__awaitConfirm__',
    });
  }, [dispatch]);

  const ladderFor = useCallback((sourceNodeId) => {
    const map = confirmLadders.current;
    let entry = map.get(sourceNodeId);
    if (entry) return entry;
    entry = { ladder: null, value: undefined };
    entry.ladder = createAwaitConfirm({
      onStatus: (status) => {
        if (!status) return;
        if (status.state === 'pending') {
          deliverSubmitStatus(sourceNodeId, true);
          return;
        }
        // confirmed OR timeout both END the submitting state; only confirmed
        // fires the source node's `confirmed` signal.
        deliverSubmitStatus(sourceNodeId, false);
        if (status.state === 'confirmed') {
          emitRef.current?.(sourceNodeId, 'confirmed', { value: entry.value });
        }
      },
    });
    map.set(sourceNodeId, entry);
    return entry;
  }, [deliverSubmitStatus]);

  useEffect(() => () => {
    for (const entry of confirmLadders.current.values()) entry.ladder.cancel();
    confirmLadders.current.clear();
  }, []);

  /**
   * THE HOST-ACTION SUBMITTING SPINNER (§5, immediate-ui-response — for a
   * latency-bearing HOST ACTION, not a write).
   *
   * A `<button>.activated → host.openMainSession` (or `openSession`) leaves the
   * screen unchanged for a beat while the app opens the session — the very "did
   * my tap register?" gap §5 forbids. The confirm ladder above cannot cover it: a
   * host action's proof never comes back on the pushed feed (`observeFeed`), it
   * comes back as the app's own acknowledgement. So this is the confirm ladder's
   * PEER for those host actions — it delivers the SAME universal
   * `submitStatus{submitting}` slot to the emitting node the frame the wire is
   * taken, and clears it (submitting: false) on the op's `host.hostResult` ack or
   * a fallback timeout (the confirm ladder's, so a stuck spinner can never remain).
   *
   * ⚠️ `openCapture` is DELIBERATELY EXCLUDED. It does not do async work under the
   * button — it opens an INTERACTIVE picker (option sheet → pick image → Save), so
   * a button spinner would wrongly cover the user's own multi-step interaction (it
   * would sit spinning behind the sheet and clear exactly when Save completes). The
   * capture's post-Save "processing" surfaces as the §6 image-placeholder PENDING
   * card the host pushes on Save (`pending.adds` → PendingCard), not on the button.
   *
   * It is a KIT DEFAULT: any node wired to one of these ops arms it, no blueprint
   * opt-in — so the whole fleet's session-open buttons acknowledge the tap without
   * a re-pin. Only a component that draws `submitStatus` (the generic Button)
   * shows it; a Card or the `host` pseudo-node wired onward is a harmless no-op.
   */
  const HOST_ACTION_OPS = useMemo(() => new Set(['openMainSession', 'openSession']), []);
  const hostActionTimers = useRef(new Map()); // sourceNodeId → fallback timeout handle

  const clearHostActionSpinners = useCallback(() => {
    const map = hostActionTimers.current;
    if (!map.size) return;
    for (const [sourceNodeId, timer] of map) {
      clearTimeout(timer);
      deliverSubmitStatus(sourceNodeId, false);
    }
    map.clear();
  }, [deliverSubmitStatus]);

  const armHostActionSpinner = useCallback((sourceNodeId) => {
    const map = hostActionTimers.current;
    const prev = map.get(sourceNodeId);
    if (prev) clearTimeout(prev);
    deliverSubmitStatus(sourceNodeId, true);
    map.set(sourceNodeId, setTimeout(() => {
      map.delete(sourceNodeId);
      deliverSubmitStatus(sourceNodeId, false);
    }, AWAIT_CONFIRM_DEFAULTS.timeout_ms));
  }, [deliverSubmitStatus]);

  useEffect(() => () => {
    for (const timer of hostActionTimers.current.values()) clearTimeout(timer);
    hostActionTimers.current.clear();
  }, []);

  const emit = useCallback((nodeId, signal, payload, scopeCtx) => {
    // §5 host-action spinner CLEAR: the host acknowledging a latency-bearing
    // session-open ends the emitting button's submitting spinner, whether or not
    // the page wired the ack onward — checked here, before the no-connection
    // early-out, for exactly that reason (a page need not wire the ack).
    if (nodeId === 'host' && signal === 'hostResult' && payload && HOST_ACTION_OPS.has(payload.op)) {
      clearHostActionSpinners();
    }
    // A connection's `when` (§5) is a payload-field gate matched against THIS
    // emitting signal's raw payload — dropped here so a wire that does not
    // match its guard is as if it were never declared (a cancelled capture's
    // `{dispatched:false}` never reaches the optimistic row's slot).
    const conns = connectionsFor(index, nodeId, signal, payload)
      .filter((conn) => connectionWhenMatches(conn.when, payload));
    if (!conns.length) return;
    for (const conn of conns) {
      const dot = conn.to.indexOf('.');
      const toId = conn.to.slice(0, dot);
      const toSlot = conn.to.slice(dot + 1);

      // §5 host-action spinner ARM: a latency-bearing host action shows the
      // submitting spinner on the EMITTING button the INSTANT the wire is taken
      // — before the connection's own (possibly async) dispatch, so the tap is
      // acknowledged in the same frame. A chained op fired from `host.hostResult`
      // carries nodeId 'host' (no rendered button); it is skipped.
      if (toId === 'host' && nodeId !== 'host' && HOST_ACTION_OPS.has(toSlot)) {
        armHostActionSpinner(nodeId);
      }

      const run = () => {
        /**
         * THE CONNECTION-LEVEL PAYLOAD MAP (§5). Absent ⇒ the raw signal
         * payload passes through, exactly as before (additive, back-compat).
         * Present ⇒ the template is evaluated in the bind grammar against the
         * EMITTING node's scope — `{"$":"item.file_url"}`, `"{title}"`,
         * literals — and merged OVER the raw payload, so an explicit key wins
         * and everything the signal already carried survives.
         *
         * This is what makes the generic host slots reachable from a page: a
         * plain Button emits `{action}`, and the connection — not a bespoke
         * component — says which `url` that action means.
         *
         * `signal.*` IS THE EMITTING SIGNAL'S OWN PAYLOAD, and it is the half
         * that was missing. The map could read the page (`focusEntry`,
         * `page_args`, `item`) but not the value the interaction had just
         * produced, so a signal whose field name did not already match the
         * slot's was unreachable: `TextComposer.submitted {text}` could not
         * feed `host.openMainSession {prompt}`, and five blueprints wrote the
         * text into `page_args` and read it back out purely to rename it. With
         * the payload in scope the map does what it is for —
         * `{"prompt": {"$": "signal.text"}}` — and the round trip disappears.
         * Merging the map OVER the raw payload is unchanged, so an untouched
         * key still passes through.
         */
        const delivered = conn.payload
          ? {
            ...(payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}),
            ...resolveProps(conn.payload, null, {
              ...(scopeCtx || ctxRef.current),
              signal: payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : { value: payload },
            }),
          }
          : payload;
        traceRef.current?.({ from: conn.from, to: conn.to, mode: conn.mode, executor: conn.executor, payload: delivered });
        if (toId === 'host') {
          // `host.logData` carrying `awaitConfirm` arms the honest-write ladder:
          // the host callback sees the payload with `awaitConfirm` STRIPPED (its
          // contract is untouched), the SOURCE node flashes a submitting spinner,
          // and the pushed feed's arrival later fires `<sourceNode>.confirmed`.
          // A plain `logData` (no `awaitConfirm`) is byte-identical to before —
          // no ladder is created, nothing is stripped.
          let toDeliver = delivered;
          if (toSlot === 'logData' && delivered && typeof delivered === 'object'
            && !Array.isArray(delivered) && delivered.awaitConfirm) {
            const entry = ladderFor(nodeId);
            entry.value = payload && typeof payload === 'object' && !Array.isArray(payload)
              ? payload.value : undefined;
            toDeliver = entry.ladder.arm(delivered).payload;
          }
          const fn = hostRef.current?.[toSlot];
          const result = fn ? fn(toDeliver, { connection: conn, node: nodeId, signal }) : undefined;
          // A host op that answers (pick_media returning images) feeds back in
          // as `host.hostResult`, which a blueprint may chain onward.
          //
          // `request` is the payload that was DELIVERED to the slot, echoed back
          // beside the answer. An answer alone cannot say what it is an answer
          // to — a picker that returns two urls does not carry the role they
          // were picked for — so a chained slot had to re-derive it from state
          // it may no longer hold. With the request in the signal's scope, a
          // connection's payload map reads it directly
          // (`{"role_id": {"$": "signal.request.role_id"}}`), and the op-scoped
          // `host.hostResult(pickMedia)` selector says which answers it wants.
          if (result !== undefined) {
            Promise.resolve(result).then((value) => {
              if (value === undefined) return;
              emitRef.current('host', 'hostResult', { op: toSlot, result: value, request: toDeliver });
            }).catch(() => { /* a failed host op is the host's to report */ });
          }
          return;
        }
        // PAGE-LOCAL ARGS the DATA lane owns. `to: "page_args.date"` writes one
        // arg, and every selector reading it re-derives at once — the header,
        // the chart and the list move together. It is a declared connection
        // like any other: the runtime has no idea which arg matters to a widget.
        if (toId === 'page_args') {
          const value = delivered && typeof delivered === 'object'
            ? (delivered[toSlot] !== undefined ? delivered[toSlot]
              : (delivered.value !== undefined ? delivered.value : delivered.key))
            : delivered;
          pageArgsRef.current?.({ [toSlot]: value });
          return;
        }
        seq.current += 1;
        dispatch({ type: 'slot', nodeId: toId, slot: toSlot, payload: delivered, seq: seq.current, from: nodeId });
      };

      const wait = parseRate(conn.rate);
      if (wait > 0) {
        const key = `${conn.from}->${conn.to}`;
        const prev = timers.current.get(key);
        if (prev) clearTimeout(prev); // backpressure: latest
        timers.current.set(key, setTimeout(run, wait));
      } else if (conn.mode === 'sync') {
        run();
      } else {
        const key = `${conn.from}->${conn.to}:async`;
        const prev = timers.current.get(key);
        if (prev && conn.backpressure === 'latest') clearTimeout(prev);
        timers.current.set(key, setTimeout(run, 0));
      }
    }
  }, [index, dispatch, ladderFor, HOST_ACTION_OPS, armHostActionSpinner, clearHostActionSpinners]);

  useEffect(() => { emitRef.current = emit; }, [emit]);

  // LIVE UPDATE (§7.5): a new feed re-evaluates selectors upstream and lands
  // here as a fresh `ctx`. We re-render IN PLACE — no remount, so scroll,
  // filter and sheet state survive — and fire `host.dataReceived` so pages
  // wired to `applyUpdate` see the push. The SAME pushed feed is what an armed
  // `awaitConfirm` ladder watches for its written value (KG-23) — observing it
  // here is what turns a `pending` write into a `<sourceNode>.confirmed`.
  useEffect(() => {
    const feed = { entries: pageCtx.entries, pending: pageCtx.pending };
    emitRef.current('host', 'dataReceived', { feed, page_args: pageCtx.page_args });
    for (const entry of confirmLadders.current.values()) entry.ladder.observeFeed(feed);
  }, [pageCtx]);

  /**
   * THE INBOUND HALF OF THE HOST BRIDGE. `host.*` slots are the widget talking
   * to the host; a host SIGNAL is the host talking back — the nav-bar action a
   * page declared through `host.declareNavAction` being tapped
   * (`navActionTriggered`), the app reporting whether the capture a page asked
   * for actually started (`captureResult {dispatched, reason?}`), and every
   * future host-chrome event.
   *
   * `captureResult` is the one a form-shaped page cannot do without: a capture
   * the app declined (no credits) leaves the user on a page that has already
   * congratulated itself, so the page must be able to say "your photos are
   * still here" — which it can only do if the refusal reaches the blueprint.
   * The channel is generic ON PURPOSE: whatever name the bridge sends arrives
   * as `host.<signal>`, so a new host event needs a registry entry and a wire,
   * never a runtime change.
   *
   * A bridge opts in by implementing `connect(emit)`, returning its own
   * teardown; a host that does not (every inspect-only studio card, which is
   * handed `host={null}`) is simply never connected. The runtime holds no host
   * state of its own: everything arrives as a declared connection, exactly like
   * `dataReceived`.
   */
  useEffect(() => {
    if (typeof host?.connect !== 'function') return undefined;
    const off = host.connect((signal, payload) => emitRef.current?.('host', signal, payload));
    return typeof off === 'function' ? off : undefined;
  }, [host]);

  // Report anything the page could not honour (fail-closed, but visible).
  const reportRef = useRef(onReport);
  useEffect(() => { reportRef.current = onReport; }, [onReport]);
  useEffect(() => {
    if (pageCtx.__unsupported?.length) onReport?.(pageCtx.__unsupported);
  }, [pageCtx, onReport]);

  /**
   * The COMPONENT side of the same report: a node that could not resolve (a
   * composition cycle) says so through here, so the studio's fail-closed panel
   * lists it beside the data lane's unsupported selectors.
   */
  const report = useCallback((issue) => { if (issue) reportRef.current?.([issue]); }, []);

  const renderById = useCallback((id) => (id ? <IRNode key={id} nodeId={id} /> : null), []);

  /** An INLINE node (a page section written in place rather than referenced). */
  const renderNode = useCallback(
    (node, key) => (node ? <IRNode key={key} nodeId={key} node={node} /> : null),
    [],
  );

  /**
   * A grid's `card` template, rendered for ONE row. The row becomes `item` in
   * a scoped context and the template's signals route to `onSignal`, so the
   * grid can attach the identity (item_id / grid_id) the card never sees.
   *
   * `key` is the card's NODE ID as well as its React key — grid-scoped and
   * item-stable (`<gridId>__card__<itemId>`), which is what lets the studio's
   * inspect overlay select and dim an individual card: it goes through the
   * same `inspectWrap` every declared node does, rather than being invisible
   * to inspect the way the old hand-written tiles were.
   */
  const renderTemplate = useCallback((node, { item, key, onSignal } = {}) => {
    if (!node) return null;
    const rowCtx = { ...pageCtx, item };
    return (
      <NodeScope.Provider
        key={key}
        value={{
          ctx: rowCtx,
          // the ROW rides up with the signal (the grid re-emits under its own
          // id), so a connection on the grid can shape its payload from the
          // row that was actually activated
          emit: (signal, payload, emitCtx) => onSignal?.(signal, payload, emitCtx || rowCtx),
        }}
      >
        <IRNode nodeId={key} node={node} />
      </NodeScope.Provider>
    );
  }, [pageCtx]);

  // WHETHER a node's SIGNAL is wired out on THIS page — the web twin of Flutter's
  // `controller.isConnected(node.id, signal)`. `index` is already page-filtered,
  // so a wire scoped to another page never counts. A Card reads it (through
  // IRNode's `connected`) to stay inert when nothing is wired to its `activated`
  // (CONTRACT §11); it is general so any component may gate on connectivity.
  const isConnected = useCallback((nid, signal) => index.has(`${nid}.${signal}`), [index]);

  // `ui`/`setUi` are the host's or NOTHING: with no shared store the selection
  // components fall back to their own `useState`, which is the app's behaviour
  // and must stay reachable from here.
  const value = useMemo(
    () => ({
      blueprint, ctx: pageCtx, emit, slotState, renderById, renderNode, renderTemplate, host, inspect, report,
      isConnected, ui: interaction?.ui || null, setUi: interaction?.setUi || null,
    }),
    [blueprint, pageCtx, emit, slotState, renderById, renderNode, renderTemplate, host, inspect, report, isConnected, interaction],
  );

  if (!page) {
    return <UnsupportedComponent what="page" name={pageId || '(entry)'} detail="blueprint declares no pages" />;
  }

  const rootNode = blueprint.components?.[page.root];
  const bare = rootNode && rootNode.type !== 'PageShell';

  return (
    <ViewIRContext.Provider value={value}>
      <div style={{ minHeight: '100%', height: rootNode?.type === 'PageShell' && usesPageHeader(rootNode.props?.headerStyle) ? '100%' : undefined, background: 'var(--bg)' }}>
        {bare ? (
          <div style={{ padding: `16px ${bodyHorizontalInset(blueprint)}px`, display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
            <IRNode nodeId={page.root} />
          </div>
        ) : (
          <IRNode nodeId={page.root} />
        )}
      </div>
    </ViewIRContext.Provider>
  );
}
